import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
} from 'firebase/firestore';
import {
  Alert,
  Button,
  Spinner,
} from 'react-bootstrap';
import { auth, db } from '../../firebase/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import ModulePageHeader from '../common/ModulePageHeader';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import { fetchAllUnits } from '../../services/unitsService';
import { withTenantScope } from '../../services/contentWriteFields';
import { isTenantAdminForTenant } from '../../platform/membershipPermissions';
import { useConfirm } from '../../platform/confirm/ConfirmProvider';
import { useToast } from '../../platform/toast/ToastProvider';
import {
  adminCreateTenantUser,
  adminDeleteTenantUser,
  adminUpdateTenantUser,
  fetchTenantUsersForManagement,
} from '../../services/userManagementService';
import {
  PASSWORD_RESET_SENT_MESSAGE,
  sendPasswordResetForEmail,
} from '../../services/passwordResetService';
import { exportUsersToExcel } from './userExcelExport';
import { generateTempPassword } from '../../utils/userDisplay';
import { EMPTY_USER_FORM, USERS_PAGE_SIZE } from './userManagementConstants';
import { matchesUserSearch } from './userManagementSearch';
import UserManagementTable from './UserManagementTable';
import UserManagementModals from './UserManagementModals';
import './UserManagement.css';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [newUnitName, setNewUnitName] = useState('');
  const [newUnitDescription, setNewUnitDescription] = useState('');

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState(EMPTY_USER_FORM);
  const [createdPassword, setCreatedPassword] = useState('');

  const [passwordTarget, setPasswordTarget] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageIndex, setPageIndex] = useState(0);

  const { tenantId, config } = useTenant();
  const { memberships } = useMembership();
  const confirm = useConfirm();
  const toast = useToast();

  const loadUsers = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const rows = await fetchTenantUsersForManagement(tenantId);
      setUsers(rows);
    } catch (loadError) {
      setError(loadError.message || 'Napaka pri nalaganju uporabnikov.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    const init = async (user) => {
      if (!user || !tenantId) {
        setLoading(false);
        return;
      }

      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);
      const profile = docSnap.exists() ? docSnap.data() : null;
      setIsAdmin(isTenantAdminForTenant(memberships, tenantId, profile));

      const unitsList = await fetchAllUnits(tenantId);
      setUnits(unitsList);
      await loadUsers();
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      init(user);
    });

    return () => unsubscribe();
  }, [tenantId, memberships, loadUsers]);

  const filteredUsers = useMemo(
    () => users.filter((user) => matchesUserSearch(user, units, searchQuery)),
    [users, units, searchQuery],
  );

  useEffect(() => {
    setPageIndex(0);
  }, [searchQuery]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredUsers.length / USERS_PAGE_SIZE) - 1);
    if (pageIndex > maxPage) {
      setPageIndex(maxPage);
    }
  }, [filteredUsers.length, pageIndex]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PAGE_SIZE));
  const safePageIndex = Math.min(pageIndex, totalPages - 1);

  const paginatedUsers = useMemo(() => {
    const start = safePageIndex * USERS_PAGE_SIZE;
    return filteredUsers.slice(start, start + USERS_PAGE_SIZE);
  }, [filteredUsers, safePageIndex]);

  const rangeStart = filteredUsers.length === 0 ? 0 : safePageIndex * USERS_PAGE_SIZE + 1;
  const rangeEnd = Math.min((safePageIndex + 1) * USERS_PAGE_SIZE, filteredUsers.length);
  const showPagination = filteredUsers.length > USERS_PAGE_SIZE;

  const clearMessages = () => {
    setError('');
  };

  const startEditing = (userObj) => {
    clearMessages();
    setEditingUser({ ...userObj });
    setShowEditUserModal(true);
  };

  const cancelEditing = () => {
    setEditingUser(null);
    setShowEditUserModal(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEditingUser((prev) => ({ ...prev, [name]: value }));
  };

  const saveChanges = async () => {
    if (!editingUser || !tenantId) return;

    if (!editingUser.jobPosition?.trim() || !editingUser.workArea?.trim()) {
      setError('Delovno mesto in oddelek / področje dela sta obvezna.');
      return;
    }

    setBusy('save');
    clearMessages();

    try {
      await adminUpdateTenantUser({
        tenantId,
        targetUid: editingUser.id,
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        phone: editingUser.phone,
        displayName: editingUser.displayName,
        role: editingUser.role,
        unitId: editingUser.unit || '',
        jobPosition: editingUser.jobPosition || '',
        workArea: editingUser.workArea || '',
      });
      setEditingUser(null);
      setShowEditUserModal(false);
      toast.success('Uporabnik je bil posodobljen.');
      await loadUsers();
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setBusy('');
    }
  };

  const handleAddUnit = async () => {
    if (!tenantId) return;
    await addDoc(collection(db, 'units'), withTenantScope(tenantId, {
      unitName: newUnitName,
      description: newUnitDescription,
    }));
    setNewUnitName('');
    setNewUnitDescription('');
    setUnits(await fetchAllUnits(tenantId));
    toast.success('Enota je bila dodana.');
  };

  const handleDeleteUnit = async (unitId) => {
    const confirmed = await confirm({
      title: 'Izbriši enoto',
      message: 'Ali ste prepričani, da želite izbrisati to enoto?',
      confirmLabel: 'Izbriši',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'units', unitId));
      setUnits(units.filter((unit) => unit.id !== unitId));
      toast.deleted('Enota je bila izbrisana.');
    } catch (error) {
      console.error('Error deleting unit:', error);
      toast.error('Enote ni bilo mogoče izbrisati.');
    }
  };

  const handleExportUsers = async () => {
    try {
      await exportUsersToExcel({
        users,
        units,
        tenantDisplayName: config?.displayName,
      });
    } catch (exportError) {
      setError(exportError.message || 'Izvoz v Excel ni uspel.');
    }
  };

  const openAddUserModal = () => {
    clearMessages();
    setCreatedPassword('');
    setNewUserForm({
      ...EMPTY_USER_FORM,
      password: generateTempPassword(),
      unit: units[0]?.id || '',
    });
    setShowAddUserModal(true);
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    if (!tenantId) return;

    if (!newUserForm.jobPosition?.trim() || !newUserForm.workArea?.trim()) {
      setError('Delovno mesto in oddelek / področje dela sta obvezna.');
      return;
    }

    setBusy('create');
    clearMessages();

    try {
      await adminCreateTenantUser({
        tenantId,
        email: newUserForm.email,
        password: newUserForm.password,
        firstName: newUserForm.firstName,
        lastName: newUserForm.lastName,
        phone: newUserForm.phone,
        role: newUserForm.role,
        unitId: newUserForm.unit || '',
        jobPosition: newUserForm.jobPosition || '',
        workArea: newUserForm.workArea || '',
      });
      setCreatedPassword(newUserForm.password);
      toast.success('Uporabnik je bil ustvarjen.');
      await loadUsers();
    } catch (createError) {
      setError(createError.message);
    } finally {
      setBusy('');
    }
  };

  const openPasswordResetModal = (userObj) => {
    clearMessages();
    setPasswordTarget(userObj);
  };

  const handleSendPasswordReset = async () => {
    if (!passwordTarget?.email) return;

    setBusy('password');
    clearMessages();

    try {
      await sendPasswordResetForEmail(passwordTarget.email);
      toast.success(`${PASSWORD_RESET_SENT_MESSAGE} (${passwordTarget.email})`);
      setPasswordTarget(null);
    } catch (resetError) {
      setError(resetError.message);
    } finally {
      setBusy('');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget || !tenantId) return;

    setBusy('delete');
    clearMessages();

    try {
      await adminDeleteTenantUser({
        tenantId,
        targetUid: deleteTarget.id,
      });
      setDeleteTarget(null);
      toast.deleted('Uporabnik je bil izbrisan.');
      await loadUsers();
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setBusy('');
    }
  };

  if (!isAdmin) {
    return <div>Nimate dostopa do te strani.</div>;
  }

  return (
    <div className="platform-module-container user-management-container">
        <ModulePageHeader
          title="Upravljanje"
          subtitle="Uporabniki in enote vaše organizacije."
          actions={(
            <div className="user-management-header-actions">
              <Button variant="primary" onClick={openAddUserModal}>
                Dodaj uporabnika
              </Button>
              <Button variant="outline-secondary" onClick={() => setShowUnitsModal(true)}>
                Upravljanje enot
              </Button>
              <Button variant="outline-secondary" onClick={handleExportUsers}>
                Izvozi seznam
              </Button>
            </div>
          )}
        />

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <UserManagementTable
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filteredUsers={filteredUsers}
            paginatedUsers={paginatedUsers}
            users={users}
            units={units}
            safePageIndex={safePageIndex}
            totalPages={totalPages}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            showPagination={showPagination}
            onPageChange={setPageIndex}
            onEdit={startEditing}
            onPassword={openPasswordResetModal}
            onDelete={setDeleteTarget}
          />
        )}

        <UserManagementModals
          showAddUserModal={showAddUserModal}
          onCloseAddUserModal={() => setShowAddUserModal(false)}
          newUserForm={newUserForm}
          onNewUserFormChange={(patch) => setNewUserForm((prev) => ({ ...prev, ...patch }))}
          onCreateUser={handleCreateUser}
          createdPassword={createdPassword}
          busy={busy}
          showEditUserModal={showEditUserModal}
          editingUser={editingUser}
          onCancelEditing={cancelEditing}
          onEditInputChange={handleInputChange}
          onSaveChanges={saveChanges}
          passwordTarget={passwordTarget}
          onClosePasswordModal={() => setPasswordTarget(null)}
          onSendPasswordReset={handleSendPasswordReset}
          deleteTarget={deleteTarget}
          onCloseDeleteModal={() => setDeleteTarget(null)}
          onConfirmDelete={handleDeleteUser}
          showUnitsModal={showUnitsModal}
          onCloseUnitsModal={() => setShowUnitsModal(false)}
          units={units}
          newUnitName={newUnitName}
          newUnitDescription={newUnitDescription}
          onNewUnitNameChange={setNewUnitName}
          onNewUnitDescriptionChange={setNewUnitDescription}
          onAddUnit={handleAddUnit}
          onDeleteUnit={handleDeleteUnit}
        />
    </div>
  );
}

export default UserManagement;
